import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // Initialize socket connection
    const socketInstance = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setConnected(true);
      
      // Join admin room
      socketInstance.emit('join-room', 'admin');
    });

    socketInstance.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setConnected(false);
    });

    // Listen for admin events
    socketInstance.on('user-created', (data) => {
      toast.info(`New user created: ${data.user.fullName}`);
    });

    socketInstance.on('user-updated', (data) => {
      toast.info(`User updated: ${data.user.fullName}`);
    });

    socketInstance.on('user-deleted', (data) => {
      toast.info(`User deleted: ${data.userId}`);
    });

    socketInstance.on('content-approved', (data) => {
      toast.success(`Content approved: ${data.content.contentId}`);
    });

    socketInstance.on('content-rejected', (data) => {
      toast.warning(`Content rejected: ${data.content.contentId}`);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, []);

  const joinRoom = (room) => {
    if (socket) {
      socket.emit('join-room', room);
    }
  };

  const leaveRoom = (room) => {
    if (socket) {
      socket.emit('leave-room', room);
    }
  };

  const value = {
    socket,
    connected,
    joinRoom,
    leaveRoom,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export default SocketContext;